import { lazy, Suspense } from 'react'
import { createBrowserRouter, Outlet, redirect } from 'react-router'
import { NotFoundPage } from './components/error-pages/not-found-page.tsx'
import { PanelLoader } from './components/loading/panel-loader.tsx'
import { loader as rootLoader } from './routes/_root.data.ts'
import RootLayout from './routes/_root.tsx'
import { AdminOnly } from './routes/admin-only.tsx'
import AuthErrorBoundary from './routes/auth-error-boundary.tsx'
import RootErrorBoundary from './routes/error-boundary.tsx'
import InShellErrorBoundary from './routes/in-shell-error-boundary.tsx'
import ItemNotFoundErrorBoundary from './routes/item-not-found-error-boundary.tsx'

const PrivacyPolicy = lazy(() => import('./routes/privacy.tsx'))
const TermsOfService = lazy(() => import('./routes/terms.tsx'))
const Changelog = lazy(() => import('./routes/changelog.tsx'))
const OAuthReturn = lazy(() => import('./routes/oauth-return.tsx'))
const InvitePage = lazy(() => import('./routes/invite.tsx'))
const InvitationAcceptPage = lazy(() => import('./routes/invitations.tsx'))
const Home = lazy(() => import('./routes/home.tsx'))
const RoleLanding = lazy(() => import('./routes/role-landing.tsx'))
const TeamPage = lazy(() => import('./routes/team.tsx'))
const UploadPage = lazy(() => import('./routes/upload.tsx'))

const SetupLayout = lazy(() => import('./routes/setup/_layout.tsx'))
const SetupFirstRun = lazy(() => import('./routes/setup/index.tsx'))
const SetupKind = lazy(() => import('./routes/setup/kind.tsx'))
const SetupMusic = lazy(() => import('./routes/setup/music.tsx'))
const SetupBasics = lazy(() => import('./routes/setup/basics.tsx'))
const SetupPacing = lazy(() => import('./routes/setup/pacing.tsx'))
const SetupVoice = lazy(() => import('./routes/setup/voice.tsx'))
const SetupPublishing = lazy(() => import('./routes/setup/publishing.tsx'))
const SetupExemplars = lazy(() => import('./routes/setup/exemplars.tsx'))

const CampaignsPage = lazy(() => import('./routes/campaigns.tsx'))
const CampaignFlowPage = lazy(() => import('./routes/campaign-flow.tsx'))
const Settings = lazy(() => import('./routes/settings.tsx'))
const SettingsScopes = lazy(() => import('./routes/settings-scopes.tsx'))
const ReviewDetail = lazy(() => import('./routes/review/[id].tsx'))
const Activity = lazy(() => import('./routes/activity.tsx'))
const Published = lazy(() => import('./routes/published.tsx'))
const PublishedVideo = lazy(() => import('./routes/published-video.tsx'))
const PublishedProposal = lazy(() => import('./routes/published-proposal.tsx'))
const Intelligence = lazy(() => import('./routes/intelligence.tsx'))

const AssetsPage = lazy(() => import('./routes/assets/_layout.tsx'))
const AssetDetail = lazy(() => import('./routes/assets/[id].tsx'))

// Clerk auth pages - lazy loaded to keep Clerk out of the landing page bundle
const SignInPage = lazy(() => import('./routes/sign-in.tsx'))
const SignUpPage = lazy(() => import('./routes/sign-up.tsx'))

const SuspenseFallback = () => <PanelLoader label="Loading workspace" minHeight={200} className="p-0" />

function withSuspense(element: React.ReactNode) {
  return <Suspense fallback={<SuspenseFallback />}>{element}</Suspense>
}

export const router = createBrowserRouter([
  { path: '/privacy', element: withSuspense(<PrivacyPolicy />), ErrorBoundary: RootErrorBoundary },
  { path: '/terms', element: withSuspense(<TermsOfService />), ErrorBoundary: RootErrorBoundary },
  {
    path: '/changelog',
    element: withSuspense(<Changelog />),
    loader: async () => (await import('./routes/changelog.data.ts')).loader(),
    ErrorBoundary: RootErrorBoundary
  },
  { path: '/oauth/return', element: withSuspense(<OAuthReturn />), ErrorBoundary: RootErrorBoundary },
  {
    path: '/invite/:token',
    element: withSuspense(<InvitePage />),
    loader: async (args) => (await import('./routes/invite.data.ts')).loader(args),
    ErrorBoundary: RootErrorBoundary
  },
  // Outside the signed-in shell like /invite/:token: the invitee is by definition not yet a
  // member of the inviting workspace, so the root loader's session has nothing useful to say
  // here and its workspace chrome would frame a page about joining a different one.
  {
    path: '/invitations/:id',
    element: withSuspense(<InvitationAcceptPage />),
    ErrorBoundary: RootErrorBoundary
  },
  { path: '/sign-in/*', element: withSuspense(<SignInPage />), ErrorBoundary: AuthErrorBoundary },
  { path: '/sign-up/*', element: withSuspense(<SignUpPage />), ErrorBoundary: AuthErrorBoundary },
  {
    id: 'root',
    path: '/',
    element: <RootLayout />,
    loader: rootLoader,
    ErrorBoundary: RootErrorBoundary,
    children: [
      // Pathless wrapper so a thrown loader/render error below only replaces this <Outlet/>
      // inside <main> (RootLayout keeps Nav mounted) rather than bubbling to the root route's
      // ErrorBoundary above, which replaces the whole shell — see in-shell-error-boundary.tsx.
      {
        id: 'shell-content',
        element: <Outlet />,
        ErrorBoundary: InShellErrorBoundary,
        children: [
          { index: true, element: withSuspense(<RoleLanding />) },
          { path: 'queue', element: withSuspense(<Home />) },
          {
            path: 'team',
            element: withSuspense(<TeamPage />),
            loader: async () => (await import('./routes/team.data.ts')).loader()
          },
          { path: 'home', loader: async (args) => (await import('./routes/home-redirect.tsx')).loader(args) },
          {
            id: 'setup',
            path: 'setup',
            element: withSuspense(<SetupLayout />),
            loader: async () => (await import('./routes/setup/_layout.data.ts')).loader(),
            children: [
              {
                index: true,
                element: withSuspense(<SetupFirstRun />),
                loader: async () => (await import('./routes/setup/index.data.ts')).loader()
              },
              {
                path: 'kind',
                element: withSuspense(<SetupKind />),
                action: async (args) => (await import('./routes/setup/kind.data.ts')).action(args)
              },
              {
                path: 'music',
                element: withSuspense(<SetupMusic />),
                action: async (args) => (await import('./routes/setup/music.data.ts')).action(args)
              },
              {
                path: 'basics',
                element: withSuspense(<SetupBasics />),
                action: async (args) => (await import('./routes/setup/basics.data.ts')).action(args)
              },
              {
                path: 'pacing',
                element: withSuspense(<SetupPacing />),
                action: async (args) => (await import('./routes/setup/pacing.data.ts')).action(args)
              },
              {
                path: 'voice',
                element: withSuspense(<SetupVoice />),
                action: async (args) => (await import('./routes/setup/voice.data.ts')).action(args)
              },
              {
                path: 'publishing',
                element: withSuspense(<SetupPublishing />),
                action: async (args) => (await import('./routes/setup/publishing.data.ts')).action(args)
              },
              {
                path: 'exemplars',
                element: withSuspense(<SetupExemplars />),
                action: async (args) => (await import('./routes/setup/exemplars.data.ts')).action(args)
              }
            ]
          },
          {
            path: 'campaigns',
            element: withSuspense(
              <AdminOnly surface="Campaign settings" belongsToAdmin="pacing, voice and publishing mode">
                <CampaignsPage />
              </AdminOnly>
            ),
            loader: async () => (await import('./routes/campaigns.data.ts')).loader()
          },
          {
            path: 'campaigns/new',
            element: withSuspense(
              <AdminOnly surface="Campaign settings" belongsToAdmin="pacing, voice and publishing mode">
                <CampaignFlowPage />
              </AdminOnly>
            ),
            loader: async (args) => (await import('./routes/campaign-flow.data.ts')).loader(args)
          },
          {
            path: 'campaigns/:id/edit',
            element: withSuspense(
              <AdminOnly surface="Campaign settings" belongsToAdmin="pacing, voice and publishing mode">
                <CampaignFlowPage />
              </AdminOnly>
            ),
            loader: async (args) => (await import('./routes/campaign-flow.data.ts')).loader(args)
          },
          {
            path: 'settings',
            element: withSuspense(<Settings />),
            loader: async () => (await import('./routes/settings.data.ts')).loader()
          },
          { path: 'settings/scopes', element: withSuspense(<SettingsScopes />) },
          {
            // RAL-156: "/" is the Queue view now — /review's held/snoozed list moved there (as a
            // `?filter=` search param, the same contract this route used to expose), so /review
            // just forwards here rather than rendering its own duplicate list. /review/:assetId/:id
            // (the full decision detail page) is unaffected and still its own route below.
            path: 'review',
            loader: ({ request }) => {
              const url = new URL(request.url)
              const target = url.searchParams.get('filter') === 'snoozed' ? '/queue?filter=snoozed' : '/queue'
              return redirect(target)
            }
          },
          {
            path: 'review/:assetId/:id',
            element: withSuspense(<ReviewDetail />),
            loader: async (args) => (await import('./routes/review/[id].data.ts')).loader(args),
            action: async (args) => (await import('./routes/review/[id].data.ts')).action(args),
            ErrorBoundary: ItemNotFoundErrorBoundary
          },
          {
            path: 'activity',
            element: withSuspense(
              <AdminOnly surface="Agent activity" belongsToAdmin="the agent's run log and its controls">
                <Activity />
              </AdminOnly>
            ),
            loader: async (args) => (await import('./routes/activity.data.ts')).loader(args)
          },
          {
            path: 'published',
            element: withSuspense(<Published />),
            loader: async () => (await import('./routes/published.data.ts')).loader()
          },
          {
            path: 'published/video/:id',
            element: withSuspense(<PublishedVideo />),
            loader: async (args) => (await import('./routes/published-video.data.ts')).loader(args)
          },
          {
            path: 'published/proposal/:assetId/:id',
            element: withSuspense(<PublishedProposal />),
            loader: async (args) => (await import('./routes/published-proposal.data.ts')).loader(args)
          },
          { path: 'intelligence', element: withSuspense(<Intelligence />) },
          { path: 'upload', element: withSuspense(<UploadPage />) },
          {
            path: 'assets',
            element: withSuspense(<AssetsPage />),
            loader: async () => (await import('./routes/assets/_layout.data.ts')).loader()
          },
          {
            path: 'assets/:id',
            element: withSuspense(<AssetDetail />),
            loader: async (args) => (await import('./routes/assets/[id].data.ts')).loader(args),
            ErrorBoundary: ItemNotFoundErrorBoundary
          },
          // Catch-all: a genuinely unmatched path under the signed-in shell (RAL-162's
          // `unknown_route` reason) — previously fell through to React Router's unstyled
          // default "no route matched" page.
          { path: '*', element: <NotFoundPage reason="unknown_route" /> }
        ]
      }
    ]
  }
])
